import { useState, useEffect, useRef } from "react";
import { calculateSizes } from "./utils";
import ArtworkAlert from "../ArtworkAlert.vue";
import { useDtfStore } from "../store";
import "./Height.scss";
import { renderList } from "common/vue/render-list";
import { cn } from "tailwind-variants";

function Height({ className, ...restProps }) {
  const [customHeight, setCustomHeight] = useState(null);
  const [showCustomSize, setShowCustomSize] = useState(false);
  const timerRef = useRef(null);

  const sizes = [
    {
      label: "4 Inches",
      value: 4
    },
    {
      label: "6 Inches",
      value: 6
    },
    {
      label: "8 Inches",
      value: 8
    },
    {
      label: "10 Inches",
      value: 10
    }
  ];

  const store = useDtfStore();

  function letterNumberArtwork() {
    return store.letterNumberArtwork;
  }

  function isSelected(size) {
    return letterNumberArtwork().height === size.value && !showCustomSize;
  }

  function loadFromConfig() {
    return store.loadFromConfig;
  }

  function dtfMinHeight() {
    return store.dtfMinHeight;
  }

  function dtfMaxHeight() {
    return store.dtfMaxHeight;
  }

  function isValidCustomSize() {
    return (
      Number(letterNumberArtwork().height) >= dtfMinHeight() &&
      Number(letterNumberArtwork().height) <= dtfMaxHeight()
    );
  }

  function updateHeight(height) {
    const artworkSizes = calculateSizes({
      ...letterNumberArtwork(),
      height
    });
    const isValid = isValidCustomSize();
    store.updateArtworkValues({
      index: 0,
      isValid,
      height,
      sizes: artworkSizes
    });
    if (isValid && Object.keys(artworkSizes).length) {
      store.getArtworksPricing();
    }
    store.updateJerseyNumberPdpDates();
  }

  function handleSelect(size) {
    setShowCustomSize(false);
    setCustomHeight(null);
    const height = size.value;
    updateHeight(height);
  }

  function handleCustomSelect() {
    store.updateArtworkValues({
      index: 0,
      isValid: false
    });
    setShowCustomSize(true);
  }

  function handleCustomInputChange(event, callback) {
    clearTimeout(timerRef.current);
    const { value } = event.target;
    let inputValue = value.match(/(\d+(\.\d*)?)/);
    inputValue = inputValue ? inputValue[0] : "0";
    const splitHeight = inputValue.split(".");
    if (splitHeight[1] && splitHeight[1].length > 2) {
      inputValue = Number([splitHeight[0], splitHeight[1].slice(0, 2)].join("."));
    }
    event.target.value = inputValue;
    timerRef.current = setTimeout(() => {
      callback(inputValue);
    }, 1000);
  }

  const { height } = letterNumberArtwork();
  const prevHeightRef = useRef(height);

  useEffect(() => {
    if (prevHeightRef.current && height === null) {
      setCustomHeight(null);
      setShowCustomSize(false);
    }
    prevHeightRef.current = height;
  }, [height]);

  useEffect(() => {
    const presetHeight = sizes.find(size => size.value === letterNumberArtwork().height);
    if (!loadFromConfig() && !presetHeight) {
      setShowCustomSize(true);
      setCustomHeight(letterNumberArtwork().height);
    }
    const isValid = isValidCustomSize();
    store.updateArtworkValues({
      index: 0,
      isValid
    });
  }, []);

  useEffect(
    () => () => {
      clearTimeout(timerRef.current);
    },
    []
  );

  return (
    <div {...restProps} className={cn("jersey-numbers-height", className)}>
      <div className="jersey-numbers-height__sizes">
        {renderList(sizes, size => (
          <div
            className={cn(
              "jersey-numbers-height__size",
              isSelected(size) && "jersey-numbers-height__size--selected"
            )}
            key={size.value}
            onClick={() => {
              handleSelect(size);
            }}
            data-testid="height-div"
          >
            {size.label}
          </div>
        ))}
        <div
          className={cn(
            "jersey-numbers-height__size",
            showCustomSize && "jersey-numbers-height__size--selected"
          )}
          onClick={() => {
            handleCustomSelect();
          }}
          data-testid="height-div-1"
        >
          {" "}
          Custom size{" "}
        </div>
      </div>
      {showCustomSize ? (
        <>
          <p className="jersey-numbers-height__label">Height (inches)</p>
          <input
            className="jersey-numbers-height__input"
            value={customHeight}
            onInput={e => {
              setCustomHeight(e.target.value);
              handleCustomInputChange(e, updateHeight);
            }}
            maxLength="4"
            type="tel"
            data-testid="jersey-numbers-height-input"
          />
          {customHeight && !isValidCustomSize() ? (
            <ArtworkAlert
              className="jersey-numbers-height__error"
              data-testid="jersey-numbers-height-error"
            >
              {"Please enter a valid height between"}
              {dtfMinHeight()}
              {"and"}
              {dtfMaxHeight()}
              {"inches"}
            </ArtworkAlert>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

export default Height;
